import { CourseUseCases } from "../../usecase/impl/courseUseCases";
import { ContentProvider, ContentProviderType } from "./content-provider";
import {
  Content,
  ContentAnnotation,
  ContentComplement,
  ContentDoubt,
  Question,
  QuestionStatus,
  ScormCheckpoint
} from "../../../infrastructure/presentation/views/content/helpers";
import {Rating, RatingList} from "../rating";
import { CourseContentUseCase } from "../../usecase/content_provider/course/content_interface";
import { Instructor } from "../entities";

export class CourseContentProvider implements ContentProvider {
  constructor(
    id: number,
    courseUseCase: CourseUseCases,
    contentUseCase: CourseContentUseCase
  ) {
    this._courseUseCase = courseUseCase;
    this._contentUseCase = contentUseCase;
    this._id = id;
  }

  private _courseUseCase: CourseUseCases;
  private _contentUseCase: CourseContentUseCase;

  private readonly _id: number;

  getID(): number {
    return this._id;
  }

  getType(): ContentProviderType {
    return ContentProviderType.COURSE;
  }

  async getAllContents(): Promise<Content[]> {
    return await this._contentUseCase.getAllContents(this._id);
  }

  async initializeContent(contentID: number): Promise<void> {
    return await this._contentUseCase.initializeContent(this._id, contentID);
  }

  async getContentByID(contentID: number): Promise<Content> {
    return await this._contentUseCase.getContentByID(this._id, contentID);
  }

  async getAllComplements(contentID: number): Promise<ContentComplement[]> {
    return await this._contentUseCase.getAllComplements(this._id, contentID);
  }

  async createRating(contentID: number, rating: Rating): Promise<Rating> {
    return await this._contentUseCase.createRating(this._id, contentID, rating);
  }

  async getAllRatings(contentID: number,  index?: number): Promise<RatingList> {
    return await this._contentUseCase.getAllRatings(this._id, contentID, index);
  }

  async likeRating(ratingID: number): Promise<void> {
    return await this._contentUseCase.likeRating(ratingID);
  }

  async removeLikeRating(ratingID: number): Promise<void> {
    return await this._contentUseCase.removeLikeRating(ratingID);
  }

  async deleteRating(contentID: number, ratingID: number): Promise<void> {
    return await this._contentUseCase.deleteRating(this._id, contentID, ratingID);
  }

  async createDoubt(
    contentID: number,
    contentDoubt: ContentDoubt
  ): Promise<ContentDoubt> {
    return await this._contentUseCase.createDoubt(
      this._id,
      contentID,
      contentDoubt
    );
  }

  async getAllDoubts(contentID: number): Promise<ContentDoubt[]> {
    return await this._contentUseCase.getAllDoubts(this._id, contentID);
  }

  async getContentInstructors(contentID: number): Promise<Instructor[]> {
    return await this._contentUseCase.getContentInstructors(
      this._id,
      contentID
    );
  }

  async createContentAnnotation(
    contentID: number,
    annotation: ContentAnnotation
  ): Promise<ContentAnnotation> {
    return await this._contentUseCase.createContentAnnotation(
      this._id,
      contentID,
      annotation
    );
  }

  async getAllContentAnnotations(
    contentID: number
  ): Promise<ContentAnnotation[]> {
    return await this._contentUseCase.getAllContentAnnotations(
      this._id,
      contentID
    );
  }

  async updateContentAnnotation(
    contentID: number,
    annotation: ContentAnnotation
  ): Promise<ContentAnnotation> {
    return await this._contentUseCase.updateContentAnnotation(
      this._id,
      contentID,
      annotation
    );
  }

  async deleteContentAnnotation(
    contentID: number,
    annotationID: number
  ): Promise<void> {
    return await this._contentUseCase.deleteContentAnnotation(
      this._id,
      contentID,
      annotationID
    );
  }

  async saveCheckpointSeconds(
    contentID: number,
    timestamp: number
  ): Promise<void> {
    return await this._contentUseCase.saveCheckpointSeconds(
      this._id,
      contentID,
      timestamp
    );
  }

  async saveCheckpointSCORM(
    contentID: number,
    scormCheckpoint: ScormCheckpoint
  ): Promise<void> {
    return await this._contentUseCase.saveCheckpointSCORM(
      this._id,
      contentID,
      scormCheckpoint
    );
  }

  async saveProgress(contentID: number, speedRate: number): Promise<number> {
    return await this._contentUseCase.saveProgress(
      this._id,
      contentID,
      speedRate
    );
  }

  async generateQuestionnaire(contentID: number): Promise<number> {
    return await this._contentUseCase.generateQuestionnaire(
      this._id,
      contentID
    );
  }

  async getQuestionByPage(
    contentID: number,
    questionnaireID: number,
    page: number
  ): Promise<Question> {
    return await this._contentUseCase.getQuestionByPage(
      this._id,
      contentID,
      questionnaireID,
      page
    );
  }

  async replyQuestionByPage(
    contentID: number,
    questionnaireID: number,
    page: number,
    alternativeID: number
  ): Promise<QuestionStatus> {
    return await this._contentUseCase.replyQuestionByPage(
      this._id,
      contentID,
      questionnaireID,
      page,
      alternativeID
    );
  }

  async replyDescriptiveQuestionByPage(contentID: number, questionnaireID: number, page: number, answer: string): Promise<QuestionStatus> {
    return await this._contentUseCase.replyDescriptiveQuestionByPage(
      this._id,
      contentID,
      questionnaireID,
      page,
      answer
    );
  }

  async finishContent(contentID: number): Promise<void> {
    return await this._contentUseCase.finishContent(this._id, contentID);
  }

  async finishContentProvider(): Promise<void> {
    return await this._courseUseCase.finishCourse(this._id);
  }
}
